/**
 * Shared between the two Data Curation tabs.
 *
 * Clips (WearerHandsPage) cuts density windows out of videos; Datasets
 * (DatasetPage) ships whole videos to a lab. They disagree about almost
 * everything downstream, but they read the same /videos payload, run the same
 * description search, and offer the same task vocabulary — so those live here
 * rather than being defined twice and drifting.
 */
import { API_BASE_URL } from '../config';

export interface WHEvent {
  start: number;
  end: number;
  score: number;
  hands: number;
  // Of the seconds inside this event that were actually sampled, the fraction
  // that had hands. Density against wall-clock seconds is not usable here: the
  // ingest motion gate embeds only a few percent of a video, so a long event
  // can never score high however solid the activity is.
  seenDensity: number;
}

export interface WHVideo {
  filename: string;
  orgId: string;
  orgName: string;
  // Organizations.industry, resolved server-side: absent => 'Private Security',
  // plix / Plix-sales => 'Internal'. Always a non-empty string.
  industry: string;
  duration: number;
  resolution: string;
  handSeconds: number[];
  negSeconds: number[];
  events: WHEvent[];
  nightMedian: number | null;
}

export interface VideosResponse {
  generatedAt: string;
  hours: number;
  nightThreshold: number | null;
  videos: WHVideo[];
  excluded: { [reason: string]: number };
  /** How far back the hands store retains. `hours=0` reaches this far, not "all time". */
  storeRetentionDays?: number;
}

/**
 * The Analysis corpus: every video the fleet recorded in the window, graded for
 * delivery. Not the wearer-hands store — no hand-detection requirement and no
 * retention horizon, so a rate computed off this is the fleet's, not a slice's.
 *
 * Rows are tuples with the org interned into `orgs`: the same ~95 auth0 subject
 * strings repeated across 58k rows was a third of the payload for nothing.
 */
/**
 * The fields the shared panels need off a video, whichever corpus it came from.
 * WHVideo (hands store) and an expanded CorpusResponse row both satisfy it.
 */
export interface CorpusVideo {
  filename: string;
  orgId: string;
  orgName: string;
  industry: string;
  duration: number;
  /**
   * 1 = 1080p delivery grade · 2 = 720p at the same frame rate ·
   * 3 = 1080p under the fps gate · 4 = 720p under it ·
   * 0 = below both (or unreadable) · -1 = unprobed.
   * Absent on hands-store rows. Only `=== 1` means deliverable.
   */
  grade?: number;
}

export interface CorpusResponse {
  status: 'ready' | 'stale' | 'warming';
  /** [orgId, orgName, industry] */
  orgs: [string, string, string][];
  /** [filename, orgIndex, seconds, grade] — see CorpusVideo.grade */
  videos: [string, number, number, number][];
  coverage: { total: number; probed: number; pending: number };
  /** Oldest capture day the corpus holds (YYYY-MM-DD) — the 1080p rollout. */
  since?: string;
  /** Calendar days the corpus SPANS. Grows daily; not the rate denominator. */
  windowDays?: number;
  /**
   * Trailing days the collection rate is measured over. Split from windowDays
   * when the corpus stopped being a trailing window: dividing all-time hours by
   * an all-time span averages in the pre-rollout days that hold no 1080p.
   */
  rateDays?: number;
  grade?: { resolution: string; minFps: number };
  generatedAt?: string;
  /** Devices whose Devices doc carries `hasIMU: true` — a confirmed gyroscope. */
  imuDevices?: string[];
  /** How far IMU confirmation has got; null when the backend could not say. */
  imu?: {
    confirmed: number;
    asked: number;
    verdicts: { [verdict: string]: number };
    probing: boolean;
  } | null;
}

export interface SearchHit {
  filename: string;
  timestamp: number;
  /** Absent on a slim search — see runDescriptionSearch. */
  description?: string;
  similarity: number;
  userId: string;
}

// ---------- Datasets tab ----------

/** One video's coarse setting. `source` is part of the answer, not decoration:
 *  'visual' was read off the footage; 'org' is this customer's own typical
 *  setting, and 'industry' their industry's, used because the clip was night/IR
 *  or the margin was a coin flip. Only 'visual' is an observation of this clip. */
export interface EnvLabel {
  environment: string;
  source: 'visual' | 'org' | 'industry';
  confidence: number | null;
  /**
   * Present when a dedicated indoor/outdoor CLIP pair (AUC 0.910) disagrees with
   * the label but not strongly enough for the backend to override it. The value
   * is the signed contrast; negative means the frames look outdoors. Hand ground
   * truth on 124 videos found 7 of 10 such videos really were mislabelled, so
   * this marks footage worth excluding rather than footage known to be wrong.
   */
  contested?: number;
  /** Set when the backend DID override; the label the classifier had picked. */
  wasVisual?: string;
}

export interface EnvCoverage {
  videos: number;
  scored: number;
  visual: number;
  pending: number;
  marginCutoff: number;
  labels: string[];
}

export interface VisualHit {
  filename: string;
  orgId: string;
  score: number;
  timestamps: number[];
  /** Every comma-separated term that found this video. A video can match more
   *  than one, and it is stored once. */
  matchedTerms: string[];
}

/**
 * One video in the delivery cart.
 *
 * Carries its own duration/industry/org rather than looking them up in the
 * current /videos payload: the whole point of a cart is to accumulate across
 * several searches and time windows, so an item added under one filter must
 * still meter correctly after the filter moves on.
 *
 * `addedVia` records the search that produced it, which is what makes a cart
 * assembled from "cars", "garages" and "spraying" legible afterwards — and it
 * ships as `source_query` in the manifest, so the lab gets the provenance too.
 */
export interface CartItem {
  filename: string;
  duration: number;
  industry: string;
  orgId: string;
  orgName: string;
  addedVia: string;
  /** The individual comma-separated terms that matched this video, when it came
   *  in through a search. This is what the max-hours balancer groups by — the
   *  whole `addedVia` string can't tell "garage" footage from "parking" footage
   *  inside one union search. Absent for videos added by plain filtering. */
  terms?: string[];
  /** Best search similarity at add time. When a cap forces trimming, the
   *  strongest matches are the ones kept. */
  score?: number;
  /** Overrides `score` as the packer's tiebreak when the choice depends on more
   *  than how well a video matches. A migration sets it: taking footage OUT of
   *  a bucket has to weigh the donor's own mix, not just the destination's. */
  rank?: number;
}

export const CART_KEY = 'ds-cart-v1';

export interface ManifestRow {
  filename: string;
  environment: string;
  source_query: string | null;
  environment_source: string;
  environment_confidence: number | null;
  task: string | null;
  industry: string;
  org_id: string;
  duration_s: number | null;
  size_bytes: number | null;
  captured_at_local: string | null;
  resolution: string | null;
  fps: number | null;
  codec: string | null;
  night: boolean;
}

export interface Totals {
  videos: number;
  hours: number;
  seconds: number;
  bytes: number;
  probed: number;
}

export interface DeliveryJob {
  id: string;
  name: string;
  container: string;
  /** Set when this delivery poured into an existing bucket instead of minting
   *  its own. Deleting such a job removes only its record — the bucket and its
   *  videos belong to the original delivery. */
  appendTo?: string | null;
  /** Who the bucket is for. Display form; the audit log keys on it. */
  customer?: string;
  customerDisplay?: string;
  status: 'queued' | 'running' | 'done' | 'error' | 'cancelling' | 'cancelled';
  createdAt: number;
  /** Which half of the run is active. Copies are issued first and confirmed
   *  after, so `copied` is 0 for the whole issuing phase — the phase is what
   *  tells the UI which counter is currently the live one. */
  phase?: 'queued' | 'copying' | 'verifying' | 'done';
  totals: Totals;
  /** How many of the selection were already in the target bucket and so never
   *  copied. Append deliveries only; 0 for a fresh bucket. */
  skippedExisting?: number;
  /** How many were dropped for already sitting in the OTHER bucket of a paired
   *  collection — the disjointness pass, re-checked server-side at submit. */
  skippedElsewhere?: number;
  /** Duplicate rows removed from the merged manifest before upload. Always 0
   *  unless something upstream regressed. */
  dupesDropped?: number;
  /** Set when this delivery moved footage out of another bucket: what the
   *  donor gave up, recorded once the copy had verifiably landed. */
  migrateFrom?: string | null;
  migrated?: { removed: number; container: string; remaining?: number };
  migrateError?: string | null;
  /** The bucket's manifest totals either side of this job — what it held before
   *  and what it holds now. Filled in when the job finishes, so a delivery can
   *  report the movement it caused and not just the cargo it carried. */
  bucketBefore?: Totals | null;
  bucketAfter?: Totals | null;
  /** The cart state that produced this delivery, stored verbatim. Only the
   *  co-collect stamp is read back by the UI. */
  filters?: {
    coCollect?: { side?: string; pairedWith?: string };
    [k: string]: unknown;
  };
  // `issued` counts copies Azure has ACCEPTED; `copied` counts bytes that have
  // actually landed. They differ for as long as a copy is in flight, which is
  // why the job is not done when the last one is issued.
  progress: {
    issued: number;
    copied: number;
    failed: number;
    metadata: number;
    total: number;
  };
  sasUrl: string | null;
  sasExpiresAt?: number;
  error: string | null;
}

/** The cart's org and category panels, frozen at delivery time.
 *
 * Computed server-side from the rows that shipped, so it describes the
 * container rather than the cart — see `_breakdown` in wh_delivery.py for why
 * the category shares can differ slightly from the checkout preview. */
export interface BucketBreakdown {
  orgs: { orgId: string; orgName: string; videos: number; seconds: number; pct: number }[];
  categories: { term: string; videos: number; seconds: number; pct: number }[];
  /** Where it was shot. A partition like orgs — one environment per video —
   *  and absent on buckets delivered before it was recorded. */
  environments?: {
    environment: string;
    source?: string;
    videos: number;
    seconds: number;
    pct: number;
  }[];
  seconds: number;
  videos: number;
}

/** A delivery container as it exists in Azure right now. Sourced from the
 *  storage account rather than our job records, because the container is what
 *  the lab pulls from and the two can disagree. */
export interface Bucket {
  container: string;
  name: string;
  videos: number;
  bytes: number;
  hasManifest: boolean;
  /** This container's OWN footage is redacted — distinct from `redacted`,
   *  which is a separate partner container. True for a redacted delivery
   *  re-shared to another customer, which has no unredacted twin. */
  contentsRedacted?: boolean;
  lastModified: string | null;
  jobId: string | null;
  status: string | null;
  hours: number | null;
  /** From the job record; the durable copy lives in the audit log. */
  customer?: string | null;
  createdBy?: string | null;
  /** Absent on buckets delivered before this was recorded — the rows were never
   *  kept anywhere it could be recomputed from, so those stay blank. */
  breakdown?: BucketBreakdown | null;
  /** The face-blurred copy of this bucket, when the redaction pipeline has run
   *  over it. Null — never an empty object — for buckets that have none, which
   *  is what the panel keys off to render nothing rather than a dead link.
   *  `videos` is counted from the redacted container itself, so comparing it
   *  with `videos` above is what shows whether the run finished. */
  redacted?: { container: string; videos: number; bytes: number } | null;
}

/** A bucket we only ever READ — reference footage that lives outside the
 *  delivery pipeline (today: GCS). It is a separate type from Bucket on
 *  purpose: every control in the Buckets panel that can mutate a container is
 *  typed against Bucket, so one of these can never be passed to delete,
 *  balance, top-up or the exclusion set. The backend keeps the same split,
 *  under its own `external` key.
 *
 *  `videos` is null when the listing failed; `error` then says why. A failed
 *  row still renders — a row that vanished would read as "the data is gone". */
export interface ExternalBucket {
  id: string;
  name: string;
  bucket: string;
  prefix: string;
  /** Deep link to the same view in the Cloud console. */
  url: string;
  videos: number | null;
  objects: number | null;
  bytes: number | null;
  /** The listing hit its page cap, so the counts are a floor, not a total. */
  truncated: boolean;
  error: string | null;
  /** Opted in, per bucket, to being mirrored into Azure and shared by SAS.
   *  Off by default so a new reference bucket cannot become shareable by
   *  being added to the registry. */
  share?: boolean;
}

// Ceiling for a single activity query's hit set. It has to sit above the
// busiest query or every share computed off it is understated, silently.
//
// 2026-08-17: measured against the live index, three queries were pinned at the
// old 2000 ceiling and their true counts are 10,210 ("holding a smartphone in
// their hand"), 5,687 ("person looking at their phone") and 4,439 ("hands
// operating a machine") -- the largest was being cut by 80%. The old comment
// claimed a busiest query of ~1050, which had stopped being true. 12000 clears
// today's largest with room; the warm sweep runs slim, so lifting the ceiling
// costs fewer bytes than the truncated-but-verbose payload it replaces.
export const ACTIVITY_HIT_LIMIT = 12000;

// The ceiling for searches whose hits are READ, not counted — the Clips player
// quotes each match and the Datasets search labels its markers with it, so those
// have to stay non-slim, and a non-slim 12000 is 2.9 MB a term. Top-2000 by
// similarity is a fair cut when a person is going to scroll the result; it is
// not a fair cut when the result is a denominator, which is why the two limits
// are separate constants rather than one shared number.
export const SEARCH_HIT_LIMIT = 2000;

// Detailed activities, validated against descriptionsv1. A chip may union
// several NL phrasings when they describe the SAME activity (merges recall —
// the MiniLM 0.45 floor is phrasing-sensitive), but each chip stays one
// specific, clickable activity rather than a broad umbrella group.
// tier = robotics training value (card sort order): 1 (default) tool use /
// fine manipulation / complete tasks, 2 simple grasp-and-carry, 3 low value
// (driving, screens). Within a tier, cards sort by video count.
//
// `visual` is the category's spillover term for the everything-mix: when the
// description index has too little of a category, the mix falls back to ONE
// simple CLIP visual search with this term. Simple on purpose — visual search
// matches scenes, not hand verbs, so "lawn mower" finds mowing footage where
// "pushing a lawn mower" would average itself into nothing.
//
// The taxonomy tracks the customer ask list ("micro1 Environments
// Estimations", 2026-08; HVAC/Plumbing deliberately not covered — no such
// footage in the fleet).
export const TOP_ACTIVITIES: {
  label: string;
  queries: string[];
  tier?: number;
  visual?: string;
}[] = [
  {
    label: 'Driving',
    queries: ['hands on a steering wheel driving'],
    tier: 3,
    visual: 'driving a vehicle',
  },
  {
    label: 'Unlocking with a key',
    queries: [
      'holding a set of keys',
      'turning a key in a lock',
      'unlocking a lock with a key',
    ],
    visual: 'unlocking a door with keys',
  },
  { label: 'Opening a door', queries: ['opening a door', 'turning a door handle'], visual: 'opening a door' },
  {
    label: 'Using a phone',
    queries: ['person looking at their phone', 'holding a smartphone in their hand'],
    tier: 3,
    visual: 'person using a phone',
  },
  { label: 'Using a remote control', queries: ['holding a remote control'], tier: 3 },
  { label: 'Operating a machine', queries: ['hands operating a machine'], visual: 'operating a machine or kiosk' },
  { label: 'Inserting a card', queries: ['inserting a card into a machine'], visual: 'payment card terminal' },
  { label: 'Handling cash', queries: ['putting money into a machine'], visual: 'handling cash' },
  {
    label: 'Using a hand tool',
    // "sharpening a blade" folded in per the customer ask list (mower-blade
    // sharpening) — same activity family, not worth its own chip.
    queries: ['holding a tool and working on something', 'sharpening a blade'],
    visual: 'using hand tools',
  },
  { label: 'Working on electronics', queries: ['holding a circuit board'], visual: 'electronics repair' },
  { label: 'Filling out a form', queries: ['writing on a form with a pen'], visual: 'writing on paper' },
  {
    label: 'Handling paperwork',
    queries: ['holding papers or documents', 'holding a clipboard'],
    visual: 'holding documents',
  },
  {
    label: 'Carrying boxes',
    queries: ['carrying a stack of boxes', 'holding a package or parcel'],
    visual: 'carrying boxes',
  },
  { label: 'Opening a box', queries: ['opening a cardboard box'], visual: 'opening a box' },
  {
    label: 'Loading a vehicle',
    // truck-bed loading folded in per the customer ask list (equipment/truck
    // loading at the start of the day).
    queries: [
      'placing items into the trunk of a car',
      'opening the trunk of a car',
      'loading equipment into a truck bed',
    ],
    visual: 'loading a truck',
  },
  {
    label: 'Pushing a cart',
    queries: ['pushing a cart', 'pushing a pallet jack'],
    visual: 'pushing a cart',
  },
  {
    label: 'Spraying treatment',
    // fertilizer/herbicide handling folded in here (still separable via the raw
    // descriptions); validated in-view: fertilizer bag 0.72, herbicide bottle 0.66
    queries: [
      'holding a spray bottle',
      'holding a spray wand or sprayer',
      'holding a bag of fertilizer',
      'holding a bottle of herbicide',
    ],
    visual: 'spraying with a sprayer',
  },
  {
    label: 'Filling a sprayer tank',
    queries: ['carrying a sprayer tank', 'pouring liquid into a tank'],
    visual: 'sprayer tank',
  },
  { label: 'Wiping a surface', queries: ['wiping or cleaning a surface'], visual: 'cleaning a surface' },
  { label: 'Vacuuming', queries: ['operating a vacuum cleaner'], visual: 'vacuuming' },
  { label: 'Using a hose', queries: ['holding a hose'], visual: 'garden hose' },
  {
    label: 'Using a leaf blower',
    queries: ['holding a leaf blower', 'operating a leaf blower'],
    visual: 'leaf blower',
  },
  {
    label: 'Trimming hedges',
    queries: ['using a hedge trimmer', 'trimming a hedge'],
    visual: 'trimming hedges',
  },
  { label: 'Carrying a bag', queries: ['holding a bag'], tier: 2, visual: 'carrying a bag' },
  {
    label: 'Handling a container',
    queries: ['holding a plastic container', 'pouring a liquid'],
    visual: 'plastic container',
  },
  { label: 'Holding a water bottle', queries: ['holding a water bottle'], tier: 2, visual: 'water bottle' },
  { label: 'Putting on gloves', queries: ['putting on gloves'], visual: 'putting on gloves' },
  // ---- added 2026-08 from the customer ask list (micro1 Environments
  // Estimations). HVAC/Plumbing intentionally skipped — no such footage.
  {
    label: 'Digging and raking',
    queries: ['digging with a shovel', 'raking soil or gravel'],
    visual: 'digging with a shovel',
  },
  {
    label: 'Mowing a lawn',
    queries: ['pushing a lawn mower', 'operating a lawn mower'],
    visual: 'lawn mower',
  },
  {
    label: 'Weeding a bed',
    queries: ['pulling weeds from a garden bed', 'spreading mulch'],
    visual: 'gardening in a flower bed',
  },
  {
    label: 'Servicing a sprinkler',
    queries: ['working on a sprinkler or irrigation line'],
    visual: 'lawn sprinkler',
  },
  {
    label: 'Refueling equipment',
    queries: ['pouring fuel from a gas can', 'refueling outdoor equipment'],
    visual: 'gas can',
  },
  {
    label: 'Framing with lumber',
    queries: ['positioning wooden boards or beams', 'hammering a nail into wood'],
    visual: 'construction carpentry',
  },
  {
    label: 'Using a power tool',
    queries: ['using a cordless power drill', 'cutting metal with an angle grinder'],
    visual: 'power drill',
  },
  {
    label: 'Laying concrete or tile',
    queries: ['troweling wet concrete', 'scraping tile from a floor'],
    visual: 'concrete work',
  },
  {
    label: 'Measuring with a tape',
    queries: ['measuring with a tape measure'],
    visual: 'tape measure',
  },
  {
    label: 'Carrying a bucket',
    queries: ['carrying a bucket'],
    tier: 2,
    visual: 'carrying a bucket',
  },
  {
    label: 'Using a handheld terminal',
    queries: ['operating a handheld citation device'],
    visual: 'handheld ticketing device',
  },
  {
    // Named for the CITATION, never for the windshield. The original query,
    // 'placing a ticket on a car windshield', was 79% driving footage: every
    // "driving, with the road ahead visible through the windshield" clip landed
    // at 0.50-0.52, which clears vectordb's 0.45 floor and so counted as a
    // ticketing video. In apartment complex exterior that made the chip 85%
    // driving clips and nothing else -- a row with videos, hours and a lift
    // where no ticketing had happened at all.
    //
    // Describing the document instead of the glass fixes it outright:
    //
    //   placing a ticket on a car windshield   175 videos,  5 strong, 79% driving
    //   holding a parking citation + notice    251 videos, 10 strong,  2% driving
    //
    // Twice the genuine evidence for a fortieth of the contamination. Two
    // rejected alternatives, both measured: 'hands holding a ticket next to a
    // car' recalls best of all (0.768) but "small rectangular object ... card or
    // ticket" also describes a phone held while driving, so it returns 2,389
    // videos at 47% driving; and 'putting a citation under a windshield wiper'
    // reintroduces the same word and with it windshield-WIPING clips at 0.57.
    //
    // Volume is still capped by LazParking being ~95% undescribed -- a targeted
    // backfill of that org, not another query, is what grows this chip.
    label: 'Ticketing a vehicle',
    queries: ['holding a parking citation', 'issuing a parking violation notice'],
    visual: 'ticket on a windshield',
  },
  {
    // 'setting up barricades or signs' was dropped: 8 hits, all <= 0.49, every
    // one of them a sign in the background rather than anyone placing it.
    label: 'Setting out cones',
    queries: ['placing traffic cones'],
    visual: 'traffic cones',
  },
  {
    label: 'Working on a wheel',
    queries: [
      'attaching a wheel boot to a tire',
      'working on a car wheel with hand tools',
    ],
    visual: 'car wheel repair',
  },
  {
    label: 'Towing a vehicle',
    queries: ['operating a car jack', 'handling tow chains and straps'],
    visual: 'tow truck towing a car',
  },
];

/** Splits on commas, runs each term, and unions the results — the same rule
 *  visual search uses. One embedding of "cars, parking, garage" is not the three
 *  concepts, it is a point between them that means none of them: measured, the
 *  combined string returns 1 hit where the terms separately return 76. */
export async function runDescriptionUnion(
  query: string,
  limit: number,
): Promise<{
  hits: SearchHit[];
  perTerm: { term: string; videos: number }[];
  /** filename → every term that found it. Same shape visual search's
   *  matchedTerms gives per hit; the balancer needs it from both engines. */
  termsByFile: { [fn: string]: string[] };
}> {
  const terms = query
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, 8);
  const results = await Promise.all(terms.map((t) => runDescriptionSearch(t, limit)));
  const merged = new Map<string, SearchHit>();
  const termsByFile: { [fn: string]: string[] } = {};
  results.forEach((hits, i) =>
    hits.forEach((h) => {
      const prev = merged.get(h.filename);
      // Keep the strongest match for a video found by several terms.
      if (!prev || h.similarity > prev.similarity) merged.set(h.filename, h);
      const t = termsByFile[h.filename] || (termsByFile[h.filename] = []);
      if (!t.includes(terms[i])) t.push(terms[i]);
    }),
  );
  return {
    hits: Array.from(merged.values()).sort((a, b) => b.similarity - a.similarity),
    perTerm: terms.map((t, i) => ({
      term: t,
      videos: new Set(results[i].map((h) => h.filename)).size,
    })),
    termsByFile,
  };
}

/** @param slim drop the description text server-side. A caller that only tallies
 *  filenames should always pass this: description is ~285 bytes a hit, and the
 *  busiest query returns over ten thousand of them. Hits come back with
 *  `description` absent, not empty — read it only from a non-slim call. */
export async function runDescriptionSearch(
  query: string,
  limit: number,
  slim = false,
): Promise<SearchHit[]> {
  const resp = await fetch(`${API_BASE_URL}/api/wearer-hands/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, limit, slim }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `search failed (${resp.status})`);
  }
  return resp.json();
}

/** Classic CLIP search over frame embeddings. Runs in our own backend against
 *  Cosmos (~1-2s cross-org); it deliberately does not go through vectordb,
 *  whose /v1/videosearch is 19.3s per call and needs a per-org userId. */
export async function runVisualSearch(
  query: string,
  opts: {
    limit?: number;
    days?: number;
    excludeOrgs?: string[];
    /** Delivery containers whose contents are already spoken for. Filtered
     *  server-side so a hit that comes back is a hit you can still sell. */
    excludeBuckets?: string[];
    require1080p?: boolean;
    /** Per-term scan depth override (server clamps to 300..6000). The mix uses
     *  a shallow depth so an 8-term batch doesn't starve itself on RU. */
    rowsPerTerm?: number;
  } = {},
): Promise<{
  hits: VisualHit[];
  elapsedS: number;
  floor: number;
  terms: string[];
  perTerm: { term: string; videos: number }[];
  gatedOut: number;
  partial?: boolean;
  truncatedTerms?: string[];
  /** Hits dropped for already sitting in an excluded bucket. */
  alreadyDelivered?: number;
  bucketsUnavailable?: string[];
}> {
  const resp = await fetch(`${API_BASE_URL}/api/wearer-hands/videosearch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query,
      limit: opts.limit ?? 1000,
      days: opts.days ?? 60,
      excludeOrgs: opts.excludeOrgs ?? [],
      excludeBuckets: opts.excludeBuckets ?? [],
      require1080p: opts.require1080p ?? true,
      ...(opts.rowsPerTerm != null && { rowsPerTerm: opts.rowsPerTerm }),
    }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `visual search failed (${resp.status})`);
  }
  return resp.json();
}

/** A background exhaustive scan: walks the vector index until scores fall
 *  below the quality floor, no deadline. The interactive search shows a page;
 *  this finds the population. */
export interface DeepScanJob {
  id: string;
  status: 'queued' | 'running' | 'done' | 'error' | 'cancelled';
  query: string;
  terms: string[];
  progress?: {
    term: string;
    termIndex: number;
    terms: number;
    rows: number;
    videos: number;
    slices: number;
    slicesQueued: number;
    usableHours: number;
    /** Frames skipped for already being in an excluded bucket. */
    alreadyDelivered?: number;
  };
  perTerm?: { term: string; videos: number; exhausted: boolean }[];
  totals?: { videos: number; seconds: number; hours: number };
  gatedOut?: number;
  /** The scan stopped because it banked `capHours` of usable footage — a
   *  runaway guard, not the quality floor. */
  hoursCapped?: boolean;
  capHours?: number;
  bucketsUnavailable?: string[];
  /** Filenames found so far — only when polled with `names`. */
  hitNames?: string[];
  /** Client-side: the scan was stopped because enough footage survived the
   *  tab's filters, rather than by the row budget or the user. */
  reachedTarget?: boolean;
  error?: string;
  elapsedS?: number;
  hits?: (VisualHit & { duration: number | null })[];
}

export async function startDeepScan(
  query: string,
  opts: {
    days?: number;
    require1080p?: boolean;
    maxHours?: number;
    excludeBuckets?: string[];
  } = {},
): Promise<string> {
  const resp = await fetch(`${API_BASE_URL}/api/wearer-hands/videosearch/deep`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query,
      days: opts.days ?? 60,
      require1080p: opts.require1080p ?? true,
      ...(opts.maxHours != null && { maxHours: opts.maxHours }),
      ...(opts.excludeBuckets?.length && { excludeBuckets: opts.excludeBuckets }),
    }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `deep scan failed (${resp.status})`);
  }
  return (await resp.json()).id;
}

export async function getDeepScan(
  id: string,
  withHits: boolean,
  withNames = false,
): Promise<DeepScanJob> {
  // `names` is the middle setting: the filenames found so far, without the
  // timestamps that make the full hit list megabytes. It exists so a running
  // scan can be scored against the tab's filters on the poll loop.
  const q = withHits ? '?hits=1' : withNames ? '?names=1' : '';
  const resp = await fetch(
    `${API_BASE_URL}/api/wearer-hands/videosearch/deep/${id}${q}`,
  );
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `deep scan poll failed (${resp.status})`);
  }
  return resp.json();
}

export function fmtHours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)}m`;
  return `${h.toFixed(h < 10 ? 1 : 0)}h`;
}

export function fmtBytes(b: number): string {
  // '0 B', not a bare '0' — the unit is what makes it read as a size.
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(u.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
  return `${(b / 1024 ** i).toFixed(i >= 3 ? 1 : 0)} ${u[i]}`;
}
